import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchData, fetchGetData, getTranslatedValue, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { ToastContainer } from 'react-toastify'

interface TargetField {
  sponsertargetmasterId: number
  key: string
  label: string
  inputType: 'input' | 'radio' | 'select'
  description?: string
  iconName?: string
  isActive: boolean
}

// Flexible form values keyed by field key
type FormValues = Record<string, any>

interface ExistingTarget {
  sponsertargetId: number
  sponsertargetmasterId: number
  sponserId: number
  value: (string | number)[]
  isActive: boolean
}

interface AddPromotionProps {
  onSaveSuccess?: () => void
  existingTargets?: ExistingTarget[]
  sponserIdProp?: string
}

// Hardcoded options for known select/radio fields
const FIELD_OPTIONS: Record<string, { label: string; value: string }[]> = {
  gender: [
    { label: 'All Genders', value: '' },
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
  ],
}

const AddPromotion: React.FC<AddPromotionProps> = ({ onSaveSuccess, existingTargets = [], sponserIdProp }) => {
  const t = Tr()
  const lang = useLang()
  const location = useLocation()
  const navigate = useNavigate()
  const locationState = (location.state as { adsId?: string | number; adsData?: any } | null) || null

  const vendorData = sessionStorage.getItem('vendor')
  const vendorId = vendorData ? String(JSON.parse(vendorData).vendorId || '') : ''
  const rawSponserId = sponserIdProp || (locationState?.adsId ? String(locationState.adsId) : null)
  const sponserId = rawSponserId ? Number(rawSponserId) : null

  const [targetList, setTargetList] = useState<TargetField[]>([])
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<FormValues>({})
  const [ageErrors, setAgeErrors] = useState<Record<string, string>>({})
  const [interestList, setInterestList] = useState<any[]>([])
  const [regionList, setRegionList] = useState<any[]>([])
  const [levelList] = useState([
    { label: 'Basic', value: 'Basic' },
    { label: 'Silver', value: 'Silver' },
    { label: 'Gold', value: 'Gold' },
    { label: 'Platinum', value: 'Platinum' },
  ])

  const MIN_AGE = 0
  const MAX_AGE = 100

  // Age is optional: leaving both min and max empty means "All Ages" and is valid.
  // If either is filled, both must be whole numbers inside MIN_AGE..MAX_AGE with min < max.
  const validateAgeRange = (minRaw: any, maxRaw: any): string => {
    const min = String(minRaw ?? '').trim()
    const max = String(maxRaw ?? '').trim()

    if (!min && !max) return ''
    if (!min || !max) return t('Please enter both minimum and maximum age')
    if (!/^\d+$/.test(min) || !/^\d+$/.test(max)) return t('Age must be a whole number')

    const from = Number(min)
    const to = Number(max)
    if (from < MIN_AGE || to > MAX_AGE) {
      return `${t('Age must be between')} ${MIN_AGE} ${t('and')} ${MAX_AGE}`
    }
    if (from >= to) return t('Minimum age must be less than maximum age')
    return ''
  }

  const getInterestId = (row: any) => String(row?.interestId ?? '')
  const getInterestName = (row: any) => getTranslatedValue(lang, row, '_name') || '-'

  const getInterest = async () => {
    const res = await fetchGetData('public/interest/list')
    if (String(res?.status) === '200') {
      // this API returns the list under `message`
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.message) ? res.message : []
      setInterestList(list)
    }
  }

  const getRegion = async () => {
    const res = await fetchData('public/regionList', {})
    if (String(res?.status) === '200') {
      setRegionList(res?.data || [])
    }
  }

  const getTarget = async () => {
    setLoading(true)
    const res = await fetchGetData('public/getsponsertargetmasterlist')
    if (String(res?.status) === '200') {
      const list: TargetField[] = res?.data || []
      setTargetList(list)

      // Build a lookup from sponsertargetmasterId → existing saved target
      const savedMap: Record<number, ExistingTarget> = {}
      existingTargets.forEach((t) => { savedMap[t.sponsertargetmasterId] = t })

      const init: FormValues = {}
      list.filter((f) => f.isActive).forEach((f) => {
        const saved = savedMap[f.sponsertargetmasterId]
        if (f.inputType === 'input') {
          // Age is optional — empty min/max means "All Ages". 0/0 from the API also means "All Ages".
          const hasSavedAge =
            saved?.isActive &&
            saved.value.length >= 2 &&
            !(Number(saved.value[0]) === 0 && Number(saved.value[1]) === 0)
          init[f.key] = hasSavedAge
            ? { min: String(saved!.value[0]), max: String(saved!.value[1]) }
            : { min: '', max: '' }
        } else if (f.key === 'interest' || f.key === 'userLevel') {
          // Multi-select — empty means no filter (all interests / all levels)
          init[f.key] = saved?.isActive ? saved.value.map(String) : []
        } else if (f.key === 'region') {
          // Multi-select — empty (or the legacy 0 placeholder) means "All Regions"
          init[f.key] = saved?.isActive
            ? saved.value.map(String).filter((v) => v !== '' && Number(v) !== 0)
            : []
        } else {
          init[f.key] = saved?.isActive && saved.value.length > 0 ? String(saved.value[0]) : ''
        }
      })
      setFormValues(init)

      // Validate pre-populated age values (saved data can be out of range)
      const errs: Record<string, string> = {}
      list
        .filter((f) => f.isActive && f.inputType === 'input')
        .forEach((f) => {
          const error = validateAgeRange(init[f.key]?.min, init[f.key]?.max)
          if (error) errs[f.key] = error
        })
      setAgeErrors(errs)
    }
    setLoading(false)
  }

  useEffect(() => {
    getTarget()
    getInterest()
    getRegion()
  }, [])

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleSingleToggle = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleMultiToggle = (key: string, value: string) => {
    setFormValues((prev) => {
      const current: string[] = prev[key] || []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      return { ...prev, [key]: next }
    })
  }

  const handleAgeChange = (key: string, field: 'min' | 'max', val: string) => {
    // Keep only digits so the field can never hold a decimal, sign or exponent
    const clean = val.replace(/[^\d]/g, '')
    const next = { ...(formValues[key] || { min: '', max: '' }), [field]: clean }
    setFormValues((prev) => ({ ...prev, [key]: next }))
    setAgeErrors((errs) => ({ ...errs, [key]: validateAgeRange(next.min, next.max) }))
  }

  // ─── Payload builder ───────────────────────────────────────────────────────

  const buildTargetPayload = () => {
    const payload: Record<string, { value: (string | number)[]; isActive: boolean }> = {}
    targetList.filter((f) => f.isActive).forEach((field) => {
      const id = String(field.sponsertargetmasterId)
      const val = formValues[field.key]

      if (field.inputType === 'input') {
        // Age is optional — empty means no age filter, sent as [0, 0] / inactive
        const min = String(val?.min ?? '').trim()
        const max = String(val?.max ?? '').trim()
        payload[id] = min && max
          ? { value: [Number(min), Number(max)], isActive: true }
          : { value: [0, 0], isActive: false }
      } else if (field.key === 'interest') {
        const arr: string[] = val || []
        payload[id] = { value: arr.map((v) => Number(v)), isActive: arr.length > 0 }
      } else if (field.key === 'userLevel') {
        // Multi-select — empty means all levels, sent as an inactive empty filter
        const arr: string[] = val || []
        payload[id] = { value: arr, isActive: arr.length > 0 }
      } else if (field.key === 'region') {
        // Multi-select — empty means all regions, sent as the inactive [0] placeholder
        const arr: string[] = val || []
        payload[id] = arr.length > 0
          ? { value: arr.map((v) => Number(v)), isActive: true }
          : { value: [0], isActive: false }
      } else {
        payload[id] = { value: val ? [val] : [], isActive: !!val }
      }
    })
    return payload
  }

  const handleSubmit = async () => {
    if (!vendorId) {
      toasterrormsg(t('Vendor not found. Please login again.'))
      return
    }

    // Block saving while any age range is invalid (empty age is allowed)
    const errs: Record<string, string> = {}
    targetList
      .filter((f) => f.isActive && f.inputType === 'input')
      .forEach((f) => {
        const error = validateAgeRange(formValues[f.key]?.min, formValues[f.key]?.max)
        if (error) errs[f.key] = error
      })
    setAgeErrors(errs)
    const firstError = Object.values(errs)[0]
    if (firstError) {
      toasterrormsg(firstError)
      return
    }

    setIsSubmitting(true)
    try {
      const res: any = await fetchData('public/addeditsponsertarget', {
        vendorId,
        sponserId,
        target: buildTargetPayload(),
      })
      if (String(res?.status) === '200') {
        toastsuccessmsg(res?.message || t('Targeting saved successfully'))
        onSaveSuccess?.()
      } else {
        toasterrormsg(res?.message || t('Failed to save targeting'))
      }
    } catch {
      toasterrormsg(t('Failed to save targeting'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // ─── Field renderers ───────────────────────────────────────────────────────

  const chipStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
    borderRadius: '8px',
    padding: '8px 18px',
    cursor: 'pointer',
    background: isActive ? '#e8f4fd' : '#fff',
    color: isActive ? '#009ef7' : '#5e6278',
    fontWeight: isActive ? 600 : 400,
    fontSize: '14px',
    transition: 'all 0.15s ease',
    outline: 'none',
  })

  const renderField = (field: TargetField) => {
    const { key, label, inputType, description, sponsertargetmasterId } = field

    // AGE — two number inputs (min / max)
    if (inputType === 'input') {
      const min = formValues[key]?.min ?? ''
      const max = formValues[key]?.max ?? ''
      const error = ageErrors[key] || ''
      return (
        <div key={sponsertargetmasterId} className='mb-7'>
          <label className='form-label fw-semibold text-gray-700 mb-3'>
            {t(label)}
            <span className='text-muted fw-normal fs-8 ms-2'>({t('optional')})</span>
          </label>
          <div className='d-flex align-items-center gap-3 flex-wrap'>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-muted fs-7 fw-semibold'>{t('Min')}</span>
              <input
                type='number'
                inputMode='numeric'
                className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
                style={{ width: 90 }}
                min={MIN_AGE}
                max={MAX_AGE}
                step={1}
                placeholder={t('Any')}
                value={min}
                onChange={(e) => handleAgeChange(key, 'min', e.target.value)}
              />
            </div>
            <span className='text-muted fw-bold'>—</span>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-muted fs-7 fw-semibold'>{t('Max')}</span>
              <input
                type='number'
                inputMode='numeric'
                className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
                style={{ width: 90 }}
                min={MIN_AGE}
                max={MAX_AGE}
                step={1}
                placeholder={t('Any')}
                value={max}
                onChange={(e) => handleAgeChange(key, 'max', e.target.value)}
              />
            </div>
            <span
              className='badge fs-7 fw-semibold'
              style={{ background: '#e8f4fd', color: '#009ef7', padding: '6px 12px' }}
            >
              {min && max ? `${min} – ${max} ${t('years')}` : t('All Ages')}
            </span>
          </div>
          {error && <div className='text-danger fs-7 mt-2'>{error}</div>}
          {description && <small className='text-muted d-block mt-2'>{t(description)}</small>}
        </div>
      )
    }

    // GENDER — radio-style single-select toggle buttons
    if (inputType === 'radio') {
      const options = FIELD_OPTIONS[key] || []
      const selected: string = formValues[key] ?? ''
      return (
        <div key={sponsertargetmasterId} className='mb-7'>
          <label className='form-label fw-semibold text-gray-700 mb-3'>{t(label)}</label>
          <div className='d-flex flex-wrap gap-2'>
            {options.map((opt) => {
              const isActive = selected === opt.value
              return (
                <button
                  key={opt.value}
                  type='button'
                  onClick={() => handleSingleToggle(key, opt.value)}
                  style={{
                    border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                    borderRadius: '8px',
                    padding: '8px 22px',
                    cursor: 'pointer',
                    background: isActive ? '#e8f4fd' : '#fff',
                    color: isActive ? '#009ef7' : '#5e6278',
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '14px',
                    transition: 'all 0.15s ease',
                    outline: 'none',
                  }}
                >
                  {t(opt.label)}
                </button>
              )
            })}
          </div>
          {description && <small className='text-muted d-block mt-2'>{t(description)}</small>}
        </div>
      )
    }

    // SELECT — multi-select (interest, userLevel) or the region dropdown
    if (inputType === 'select') {
      const isMulti = key === 'interest'

      // REGION — multi-select chips fed by the regionList API
      if (key === 'region') {
        const selected: string[] = formValues[key] || []
        return (
          <div key={sponsertargetmasterId} className='mb-7'>
            <label className='form-label fw-semibold text-gray-700 mb-3'>{t(label)}</label>
            <div className='d-flex flex-wrap gap-2'>
              {/* "All Regions" clears the selection (empty = no region filter) */}
              <button
                type='button'
                onClick={() => setFormValues((prev) => ({ ...prev, [key]: [] }))}
                style={chipStyle(selected.length === 0)}
              >
                {t('All Regions')}
              </button>
              {regionList.map((row: any) => {
                const value = String(row?.regionId ?? '')
                const isActive = selected.includes(value)
                return (
                  <button
                    key={value}
                    type='button'
                    onClick={() => handleMultiToggle(key, value)}
                    style={chipStyle(isActive)}
                  >
                    {isActive && (
                      <i className='fas fa-check' style={{ fontSize: 11, color: '#009ef7' }} />
                    )}
                    {getTranslatedValue(lang, row, '_name') || '-'}
                  </button>
                )
              })}
            </div>
            {selected.length > 0 && (
              <small className='text-primary d-block mt-2 fw-semibold'>
                {selected.length} {t('selected')}
              </small>
            )}
            {description && <small className='text-muted d-block mt-1'>{t(description)}</small>}
          </div>
        )
      }

      if (isMulti) {
        const selected: string[] = formValues[key] || []
        // Interest options come from the interest/list API
        const interestOptions = interestList.map((row: any) => ({
          label: getInterestName(row),
          value: getInterestId(row),
          image: row?.image,
        }))
        if (interestOptions.length === 0) {
          return (
            <div key={sponsertargetmasterId} className='mb-7'>
              <label className='form-label fw-semibold text-gray-700 mb-3'>{t(label)}</label>
              <div className='text-muted fs-7'>{t('No interests available')}</div>
              {description && <small className='text-muted d-block mt-2'>{t(description)}</small>}
            </div>
          )
        }
        return (
          <div key={sponsertargetmasterId} className='mb-7'>
            <label className='form-label fw-semibold text-gray-700 mb-3'>{t(label)}</label>
            <div className='d-flex flex-wrap gap-2'>
              {/* "All Interests" clears the selection (empty = no interest filter) */}
              <button
                type='button'
                onClick={() => setFormValues((prev) => ({ ...prev, [key]: [] }))}
                style={{
                  border: selected.length === 0 ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                  borderRadius: '8px',
                  padding: '8px 18px',
                  cursor: 'pointer',
                  background: selected.length === 0 ? '#e8f4fd' : '#fff',
                  color: selected.length === 0 ? '#009ef7' : '#5e6278',
                  fontWeight: selected.length === 0 ? 600 : 400,
                  fontSize: '14px',
                  transition: 'all 0.15s ease',
                  outline: 'none',
                }}
              >
                {t('All Interests')}
              </button>
              {interestOptions.map((opt) => {
                const isActive = selected.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type='button'
                    onClick={() => handleMultiToggle(key, opt.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: isActive ? '2px solid #009ef7' : '1.5px solid #e4e6ef',
                      borderRadius: '8px',
                      padding: '8px 18px',
                      cursor: 'pointer',
                      background: isActive ? '#e8f4fd' : '#fff',
                      color: isActive ? '#009ef7' : '#5e6278',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '14px',
                      transition: 'all 0.15s ease',
                      outline: 'none',
                      position: 'relative',
                    }}
                  >
                    {isActive && (
                      <i className='fas fa-check' style={{ fontSize: 11, color: '#009ef7' }} />
                    )}
                    {/* {opt.image && (
                      <img
                        src={opt.image}
                        alt=''
                        style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                        onError={(e) => {
                          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    )} */}
                    {opt.label}
                  </button>
                )
              })}
            </div>
            {selected.length > 0 && (
              <small className='text-primary d-block mt-2 fw-semibold'>
                {selected.length} {t('selected')}
              </small>
            )}
            {description && <small className='text-muted d-block mt-1'>{t(description)}</small>}
          </div>
        )
      }

      // Multi-select (userLevel) — "All Levels" clears the selection
      const selected: string[] = formValues[key] || []
      return (
        <div key={sponsertargetmasterId} className='mb-7'>
          <label className='form-label fw-semibold text-gray-700 mb-3'>{t(label)}</label>
          <div className='d-flex flex-wrap gap-2'>
            <button
              type='button'
              onClick={() => setFormValues((prev) => ({ ...prev, [key]: [] }))}
              style={chipStyle(selected.length === 0)}
            >
              {t('All Levels')}
            </button>
            {levelList.map((opt) => {
              const isActive = selected.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  type='button'
                  onClick={() => handleMultiToggle(key, opt.value)}
                  style={chipStyle(isActive)}
                >
                  {isActive && (
                    <i className='fas fa-check' style={{ fontSize: 11, color: '#009ef7' }} />
                  )}
                  {t(opt.label)}
                </button>
              )
            })}
          </div>
          {selected.length > 0 && (
            <small className='text-primary d-block mt-2 fw-semibold'>
              {selected.length} {t('selected')}
            </small>
          )}
          {description && <small className='text-muted d-block mt-2'>{t(description)}</small>}
        </div>
      )
    }

    return null
  }

  // ─── Summary ───────────────────────────────────────────────────────────────

  const getSummaryValue = (field: TargetField): string => {
    const val = formValues[field.key]
    if (field.inputType === 'input') {
      const min = String(val?.min ?? '').trim()
      const max = String(val?.max ?? '').trim()
      return min && max ? `${min} – ${max}` : t('All Ages')
    }
    if (field.key === 'interest') {
      const arr: string[] = val || []
      if (arr.length === 0) return t('All Interests')
      return arr
        .map((id) => {
          const row = interestList.find((r: any) => getInterestId(r) === id)
          return row ? getInterestName(row) : id
        })
        .join(', ')
    }
    if (field.key === 'userLevel') {
      const arr: string[] = val || []
      if (arr.length === 0) return t('All Levels')
      return arr.map((level) => t(level)).join(', ')
    }
    if (field.key === 'region') {
      const arr: string[] = val || []
      if (arr.length === 0) return t('All Regions')
      return arr
        .map((id) => {
          const row = regionList.find((r: any) => String(r?.regionId) === String(id))
          return row ? getTranslatedValue(lang, row, '_name') || String(id) : String(id)
        })
        .join(', ')
    }
    return val
      ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()
      : `${t('All')} ${field.label}`
  }

  const activeFields = targetList.filter((f) => f.isActive)
  const hasAgeError = Object.values(ageErrors).some(Boolean)

  return (
    <>
      <ToastContainer />

      <div className='card card-flush py-4'>
        <div className='card-header d-flex align-items-center'>
          <div className='card-title d-flex align-items-center gap-3'>
            <span
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: '#e8f4fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <circle cx='12' cy='12' r='10' stroke='#009ef7' strokeWidth='1.8' />
                <circle cx='12' cy='12' r='3' stroke='#009ef7' strokeWidth='1.8' />
                <line x1='12' y1='2' x2='12' y2='6' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <line x1='12' y1='18' x2='12' y2='22' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <line x1='2' y1='12' x2='6' y2='12' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                <line x1='18' y1='12' x2='22' y2='12' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
              </svg>
            </span>
            <div>
              <h2 className='mb-0'>{t('Targeting Settings')}</h2>
              <p className='text-muted fs-7 mb-0'>
                {t('Define your target audience for maximum effectiveness')}
              </p>
            </div>
          </div>
        </div>

        <div className='card-body pt-5'>
          {loading ? (
            <div className='d-flex justify-content-center align-items-center py-10'>
              <span className='spinner-border spinner-border-sm text-primary me-2' />
              <span className='text-muted'>{t('Loading targeting options...')}</span>
            </div>
          ) : activeFields.length === 0 ? (
            <div
              className='d-flex flex-column align-items-center justify-content-center py-12 text-center'
              style={{ border: '1.5px dashed #e4e6ef', borderRadius: '10px', background: '#fafafa' }}
            >
              <p className='fw-semibold text-gray-600 mb-1'>{t('No Targeting Options Available')}</p>
              <p className='text-muted fs-7 mb-0'>
                {t('Your ad will be shown to all users.')}
              </p>
            </div>
          ) : (
            <>
              {activeFields.map((field) => renderField(field))}

              {/* Summary */}
              <div
                style={{
                  border: '1.5px solid #e4e6ef',
                  borderRadius: '10px',
                  padding: '16px 20px',
                  background: '#fafafa',
                  marginBottom: '16px',
                }}
              >
                <div className='fw-bold text-gray-800 mb-3'>{t('Current Targeting Summary')}</div>
                <div className='row g-2'>
                  {activeFields.map((field) => (
                    <div key={field.sponsertargetmasterId} className='col-md-6'>
                      <span className='text-muted fs-7'>{t(field.label)}: </span>
                      <span className='fw-semibold text-gray-800'>{getSummaryValue(field)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  background: '#e8f4fd',
                  border: '1px solid #b8dff7',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '13px',
                  color: '#374151',
                }}
              >
                <span className='fw-bold'>{t('Note:')} </span>
                {t(
                  'If no specific targeting is selected, your sponsored ad will be shown to all users.'
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className='d-flex justify-content-end mt-5 gap-3'>
        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => navigate('/viewsponsuredadvertisment')}
        >
          <i className='fas fa-arrow-left me-2' />
          {t('Back')}
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleSubmit}
          disabled={isSubmitting || loading || hasAgeError}
        >
          {isSubmitting ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              {t('Saving...')}
            </>
          ) : (
            <>
              {t('Save & Continue')}
              <i className='fas fa-arrow-right ms-2' />
            </>
          )}
        </button>
      </div>
    </>
  )
}

export default AddPromotion
